import { add, DateString, Days, Euros, Hours, round } from "@pentacode/openapi";
import { Balances, BonusesBalance, Employee, TimeBalance, VacationBalance } from "../model";
import { DateRange } from "./date-range";
import { toDateString } from "../utils";
import { DailyResultsDTO } from "../api/rpc";

function roundTimeBalance<T extends Omit<TimeBalance, "employeeId" | "from" | "to">>(balance: T): T {
    return {
        ...balance,
        work: round(balance.work, 2),
        absences: round(balance.absences, 2),
        adjustments: round(balance.adjustments, 2),
        actual: round(balance.actual, 2),
        nominal: round(balance.nominal, 2),
        difference: round(balance.difference, 2),
        reset: typeof balance.reset === "number" ? round(balance.reset, 2) : undefined,
        carry: round(balance.carry ?? (0 as Hours), 2),
        balance: round(balance.balance ?? (0 as Hours), 2),
    };
}

function roundVacationBalance<T extends Omit<VacationBalance, "employeeId" | "from" | "to">>(balance: T): T {
    return {
        ...balance,
        taken: round(balance.taken, 2),
        adjustments: round(balance.adjustments, 2),
        actual: round(balance.actual, 2),
        nominal: round(balance.nominal, 2),
        difference: round(balance.difference, 2),
        reset: typeof balance.reset === "number" ? round(balance.reset, 2) : undefined,
        carry: round(balance.carry || (0 as Days), 2),
        balance: round(balance.balance || (0 as Days), 2),
    };
}

function roundBonusesBalance<T extends Omit<BonusesBalance, "employeeId" | "from" | "to">>(balance: T): T {
    return {
        ...balance,
        taxed: round(balance.taxed, 2),
        untaxed: round(balance.untaxed, 2),
        actual: round(balance.actual, 2),
        nominal: typeof balance.nominal !== "undefined" ? round(balance.nominal, 2) : undefined,
        difference: typeof balance.difference !== "undefined" ? round(balance.difference, 2) : undefined,
        reset: typeof balance.reset === "number" ? round(balance.reset, 2) : undefined,
        carry: typeof balance.carry !== "undefined" ? round(balance.carry, 2) : undefined,
        balance: typeof balance.balance !== "undefined" ? round(balance.balance, 2) : undefined,
    };
}

/** @deprecated Use `roundTimeBalance`, `roundVacationBalance`, `roundBonusesBalance` instead */
export function roundBalances(balances: Omit<Balances, "employeeId" | "from" | "to">) {
    return {
        ...balances,
        time: roundTimeBalance(balances.time),
        vacation: roundVacationBalance(balances.vacation),
        bonuses: roundBonusesBalance(balances.bonuses),
    };
}

export function addTimeBalances<T extends Omit<TimeBalance, "employeeId" | "from" | "to">>(
    balanceA: T,
    balanceB: T
): T {
    return {
        ...balanceA,
        carry: add(balanceA.carry, balanceB.carry),
        actual: add(balanceA.actual, balanceB.actual),
        nominal: add(balanceA.nominal, balanceB.nominal),
        difference: add(balanceA.difference, balanceB.difference),
        work: add(balanceA.work, balanceB.work),
        absences: add(balanceA.absences, balanceB.absences),
        adjustments: add(balanceA.adjustments, balanceB.adjustments),
        balance: add(balanceA.balance, balanceB.balance),
    };
}

export function addVacationBalances<T extends Omit<VacationBalance, "employeeId" | "from" | "to">>(
    balanceA: T,
    balanceB: T
): T {
    return {
        ...balanceA,
        carry: add(balanceA.carry, balanceB.carry),
        actual: add(balanceA.actual, balanceB.actual),
        nominal: add(balanceA.nominal, balanceB.nominal),
        difference: add(balanceA.difference, balanceB.difference),
        taken: add(balanceA.taken, balanceB.taken),
        adjustments: add(balanceA.adjustments, balanceB.adjustments),
        balance: add(balanceA.balance, balanceB.balance),
    };
}

export function addBonusesBalances<T extends Omit<BonusesBalance, "employeeId" | "from" | "to">>(
    balanceA: T,
    balanceB: T
): T {
    return {
        ...balanceA,
        carry:
            typeof balanceA.carry === "number" && typeof balanceB.carry === "number"
                ? add(balanceA.carry, balanceB.carry)
                : (balanceA.carry ?? balanceB.carry),
        actual: add(balanceA.actual, balanceB.actual),
        nominal:
            typeof balanceA.nominal === "number" && typeof balanceB.nominal === "number"
                ? add(balanceA.nominal, balanceB.nominal)
                : (balanceA.nominal ?? balanceB.nominal),
        difference:
            typeof balanceA.difference === "number" && typeof balanceB.difference === "number"
                ? add(balanceA.difference, balanceB.difference)
                : (balanceA.difference ?? balanceB.difference),
        taxed: add(balanceA.taxed, balanceB.taxed),
        untaxed: add(balanceA.untaxed, balanceB.untaxed),
        balance:
            typeof balanceA.balance === "number" && typeof balanceB.balance === "number"
                ? add(balanceA.balance, balanceB.balance)
                : (balanceA.balance ?? balanceB.balance),
    };
}

/** @deprecated Use `addTimeBalance`, `addVacationBalance`, `addBonusesBalance` instead */
export function addBalances(balanceA: Balances, balanceB: Balances): Balances {
    return {
        ...balanceA,
        time: addTimeBalances(balanceA.time as TimeBalance, balanceB.time as TimeBalance),
        vacation: addVacationBalances(balanceA.vacation as VacationBalance, balanceB.vacation as VacationBalance),
        bonuses: addBonusesBalances(balanceA.bonuses as BonusesBalance, balanceB.bonuses as BonusesBalance),
    };
}

export function makeEmptyTimeBalance({ employeeId, from, to }: DateRange & { employeeId: number }): TimeBalance {
    return {
        employeeId,
        from,
        to,
        carry: 0 as Hours,
        actual: 0 as Hours,
        nominal: 0 as Hours,
        difference: 0 as Hours,
        work: 0 as Hours,
        absences: 0 as Hours,
        adjustments: 0 as Hours,
        balance: 0 as Hours,
    };
}

export function makeEmptyVacationBalance({
    employeeId,
    from,
    to,
}: DateRange & { employeeId: number }): VacationBalance {
    return {
        employeeId,
        from,
        to,
        carry: 0 as Days,
        actual: 0 as Days,
        nominal: 0 as Days,
        difference: 0 as Days,
        taken: 0 as Days,
        adjustments: 0 as Days,
        balance: 0 as Days,
    };
}

export function makeEmptyBonusesBalance({ employeeId, from, to }: DateRange & { employeeId: number }): BonusesBalance {
    return {
        employeeId,
        from,
        to,
        actual: 0 as Euros,
        taxed: 0 as Euros,
        untaxed: 0 as Euros,
    };
}

/** @deprecated Use `makeEmptyTimeBalance`, `makeEmptyVacationBalance`, `makeEmptyBonusesBalance` instead */
export function makeEmptyBalances({ employeeId, from, to }: DateRange & { employeeId: number }): Balances {
    return {
        employeeId,
        from,
        to,
        time: makeEmptyTimeBalance({ employeeId, from, to }),
        vacation: makeEmptyVacationBalance({ employeeId, from, to }),
        bonuses: makeEmptyBonusesBalance({ employeeId, from, to }),
    };
}

export function sumTimeBalances(balances: TimeBalance[]) {
    if (!balances.length) {
        return makeEmptyTimeBalance({
            employeeId: 0,
            from: "1970-01-01" as DateString,
            to: "1970-01-01" as DateString,
        });
    }
    return balances.reduce<TimeBalance>(
        (total, each) => addTimeBalances(total, each),
        makeEmptyTimeBalance(balances[0])
    );
}

export function sumVacationBalances(balances: VacationBalance[]) {
    if (!balances.length) {
        return makeEmptyVacationBalance({
            employeeId: 0,
            from: "1970-01-01" as DateString,
            to: "1970-01-01" as DateString,
        });
    }
    return balances.reduce<VacationBalance>(
        (total, each) => addVacationBalances(total, each),
        makeEmptyVacationBalance(balances[0])
    );
}

export function sumBonusesBalances(balances: BonusesBalance[]) {
    if (!balances.length) {
        return makeEmptyBonusesBalance({
            employeeId: 0,
            from: "1970-01-01" as DateString,
            to: "1970-01-01" as DateString,
        });
    }
    return balances.reduce<BonusesBalance>(
        (total, each) => addBonusesBalances(total, each),
        makeEmptyBonusesBalance(balances[0])
    );
}

/** @deprecated Use `sumTimeBalance`, `sumVacationBalance`, `sumBonusesBalance` instead */
export function sumBalances(balances: Balances[]) {
    if (!balances.length) {
        return makeEmptyBalances({ employeeId: 0, from: "1970-01-01" as DateString, to: "1970-01-01" as DateString });
    }
    return balances.reduce<Balances>((total, each) => addBalances(total, each), makeEmptyBalances(balances[0]));
}

export function aggregateTimeBalances(
    employee: Employee,
    { from, to }: DateRange,
    results: DailyResultsDTO[],
    roundValues = true,
    projected = false
): TimeBalance {
    const firstResult = results[0];
    const lastResult = results[results.length - 1];
    const today = toDateString(new Date());
    const firstBalance =
        (projected && firstResult?.date >= today && firstResult?.timeBalancePlanned) || firstResult?.timeBalance;
    const lastBalance =
        (projected && lastResult?.date >= today && lastResult?.timeBalancePlanned) || lastResult?.timeBalance;

    const balance = results.reduce(
        (total, result) => {
            const balance = (projected && result.date >= today && result.timeBalancePlanned) || result.timeBalance;
            return {
                ...addTimeBalances(total, balance),
                employeeId: employee.id,
                from,
                to,
                reset: total.reset,
                carry: total.carry,
                balance: total.balance,
            };
        },
        {
            ...makeEmptyTimeBalance({ employeeId: employee.id, from, to }),
            reset: firstBalance?.reset,
            carry: firstBalance?.carry || (0 as Hours),
            balance: lastBalance?.balance || (0 as Hours),
        } as TimeBalance
    );

    return roundValues ? roundTimeBalance(balance) : balance;
}

export function aggregateVacationBalances(
    employee: Employee,
    { from, to }: DateRange,
    results: DailyResultsDTO[],
    roundValues = true
): VacationBalance {
    const firstResult = results[0];
    const lastResult = results[results.length - 1];

    const balance = results.reduce(
        (total, result) => {
            return {
                ...addVacationBalances(total, result.vacationBalance),
                employeeId: employee.id,
                from,
                to,
                reset: total.reset,
                carry: total.carry,
                balance: total.balance,
            };
        },
        {
            ...makeEmptyVacationBalance({ employeeId: employee.id, from, to }),
            reset: firstResult?.vacationBalance.reset,
            carry: firstResult?.vacationBalance.carry || (0 as Hours),
            balance: lastResult?.vacationBalance.balance || (0 as Hours),
        } as VacationBalance
    );

    return roundValues ? roundVacationBalance(balance) : balance;
}

export function aggregateBonusesBalances(
    employee: Employee,
    { from, to }: DateRange,
    results: DailyResultsDTO[],
    roundValues = true
): BonusesBalance {
    const firstResult = results[0];
    const lastResult = results[results.length - 1];

    const balance = results.reduce(
        (total, result) => {
            return {
                ...addBonusesBalances(total, result.bonusesBalance),
                employeeId: employee.id,
                from,
                to,
                reset: total.reset,
                carry: total.carry,
                balance: total.balance,
            };
        },
        {
            ...makeEmptyBonusesBalance({ employeeId: employee.id, from, to }),
            reset: firstResult?.bonusesBalance.reset,
            carry: firstResult?.bonusesBalance.carry || (0 as Hours),
            balance: lastResult?.bonusesBalance.balance || (0 as Hours),
        } as BonusesBalance
    );

    return roundValues ? roundBonusesBalance(balance) : balance;
}

function getChunks({ from, to }: DateRange, resets: DailyResultsDTO[]): DateRange[] {
    const chunks = [];
    let chunkFrom = from;
    for (const reset of resets) {
        chunks.push({
            from: chunkFrom,
            to: reset.date,
        });
        chunkFrom = reset.date;
    }
    chunks.push({
        from: chunkFrom,
        to,
    });
    return chunks;
}

export function timeBalanceFromDailyResults(
    employee: Employee,
    range: DateRange,
    results: DailyResultsDTO[],
    roundValues = true,
    projected = false
): TimeBalance {
    const balance = aggregateTimeBalances(employee, range, results, roundValues, projected);
    const resets = results.filter((res, i) => i !== 0 && typeof res.timeBalance.reset !== "undefined");

    if (resets.length) {
        const chunks = getChunks(range, resets);

        balance.subBalances = [];

        for (const subRange of chunks) {
            balance.subBalances.push(
                aggregateTimeBalances(
                    employee,
                    subRange,
                    results.filter((res) => res.date >= subRange.from && res.date < subRange.to),
                    true,
                    projected
                )
            );
        }
    }

    return balance;
}

export function vacationBalanceFromDailyResults(
    employee: Employee,
    range: DateRange,
    results: DailyResultsDTO[],
    roundValues = true
): VacationBalance {
    const balance = aggregateVacationBalances(employee, range, results, roundValues);
    const resets = results.filter((res, i) => i !== 0 && typeof res.vacationBalance.reset !== "undefined");

    if (resets.length) {
        const chunks = getChunks(range, resets);

        balance.subBalances = [];

        for (const subRange of chunks) {
            balance.subBalances.push(
                aggregateVacationBalances(
                    employee,
                    subRange,
                    results.filter((res) => res.date >= subRange.from && res.date < subRange.to),
                    true
                )
            );
        }
    }

    return balance;
}

export function bonusesBalanceFromDailyResults(
    employee: Employee,
    range: DateRange,
    results: DailyResultsDTO[],
    roundValues = true
): BonusesBalance {
    const balance = aggregateBonusesBalances(employee, range, results, roundValues);
    const resets = results.filter((res, i) => i !== 0 && typeof res.bonusesBalance.reset !== "undefined");

    if (resets.length) {
        const chunks = getChunks(range, resets);

        balance.subBalances = [];

        for (const subRange of chunks) {
            balance.subBalances.push(
                aggregateBonusesBalances(
                    employee,
                    subRange,
                    results.filter((res) => res.date >= subRange.from && res.date < subRange.to),
                    true
                )
            );
        }
    }

    return balance;
}
